import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { find } from 'lodash';

import HeroImages from './HeroImages';
import PreviewImage from './PreviewImage';
import PhotoGalleryManager from './PhotoGalleryManager';
import UploadManager from './utils/UploadManager';

const emptyDataState = {
    originalImage: {},
    croppedImage: {}
};

export default class PhotoManagementComponent extends React.PureComponent {
    static propTypes = {
        type: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        accessToken: PropTypes.string.isRequired,
        inventoryType: PropTypes.string,
        heroEnabled: PropTypes.bool,
        galleryEnabled: PropTypes.bool
    };

    static defaultProps = {
        heroEnabled: true,
        galleryEnabled: true
    };

    constructor(props) {
        super(props);

        this.state = {
            hero: emptyDataState,
            heroOriginal: undefined,
            preview: emptyDataState,
            gallery: [],
            hasHeroImage: false
        };

        this.uploadManager = new UploadManager({
            accessToken: props.accessToken,
            entityType: props.type,
            entityId: props.id,
        });
    }

    componentDidMount() {
        this.loadMedia();
    }

    componentDidUpdate(prevProps) {
        if (this.props.id !== prevProps.id) {
            this.loadMedia();
        }
    }

    onPublish = () => {
        this.loadMedia();
    }

    onHeroPublish = (newMediaItem, heroOriginal) => {
        this.setState({
            heroOriginal,
            hasHeroImage: true
        });
        this.loadMedia();
    }

    onHeroDelete = () => {
        this.setState({
            hasHeroImage: false,
            hero: emptyDataState
        });
        this.loadMedia();
    }

    onDelete = () => {
        this.loadMedia();
    }

    onHeroPromotion = () => {
        this.loadMedia();
    }

    getHeroImage = () => {
        if (this.state.heroOriginal) {
            return {
                ...this.state.hero,
                originalImage: this.state.heroOriginal,
                croppedImage: {}
            }
        }

        return {
            ...this.state.hero,
            croppedImage: {}
        }
    };

    loadMedia = () => {
        axios
            .get(`/api/media/internal/${this.props.type}/${this.props.id}`, {
                headers: {
                    Authorization: `Bearer ${this.props.accessToken}`
                }
            })
            .then((res) => {
                if (res && !res.data.err) {
                    const photos = res.data.result || [];

                    // Set the hero photo, if it exists
                    let hero = {};
                    const heroPhoto = find(photos, { is_primary: true });
                    if (heroPhoto !== undefined) {
                        const width = parseInt(heroPhoto.width, 10);
                        const height = parseInt(heroPhoto.height, 10);

                        hero = {
                            id: heroPhoto.media_id,
                            altText: heroPhoto.title,
                            caption: heroPhoto.description,
                            credit: heroPhoto.credits,
                            position: heroPhoto.position,
                            created_date: heroPhoto.created_date,
                            updated_date: heroPhoto.updated_date,
                            is_gallery: heroPhoto.is_gallery,
                            is_preview: heroPhoto.is_preview,
                            is_primary: heroPhoto.is_primary,
                            originalImage: {
                                mediaId: heroPhoto.media_id,
                                source: heroPhoto.original_url,
                                name: heroPhoto.media_id,
                                width,
                                height
                            },
                            croppedImage: {
                                mediaId: heroPhoto.media_id,
                                source: heroPhoto.url,
                                name: heroPhoto.media_id,
                                pixelCrop: {
                                    x: heroPhoto.crop_x,
                                    y: heroPhoto.crop_y,
                                    width: heroPhoto.crop_width ? heroPhoto.crop_width : width,
                                    height: heroPhoto.crop_height ? heroPhoto.crop_height : height
                                }
                            }
                        };
                    }

                    // Set the preview photo, if it exists
                    let preview = {};
                    const previewPhoto = find(photos, { is_preview: true });
                    if (previewPhoto !== undefined) {
                        const width = parseInt(previewPhoto.width, 10);
                        const height = parseInt(previewPhoto.height, 10);

                        preview = {
                            id: previewPhoto.media_id,
                            altText: previewPhoto.title,
                            caption: previewPhoto.description,
                            credit: previewPhoto.credits,
                            position: previewPhoto.position,
                            created_date: previewPhoto.created_date,
                            updated_date: previewPhoto.updated_date,
                            is_gallery: previewPhoto.is_gallery,
                            is_preview: previewPhoto.is_preview,
                            is_primary: previewPhoto.is_primary,
                            originalImage: {
                                mediaId: previewPhoto.media_id,
                                source: previewPhoto.original_url,
                                name: previewPhoto.media_id,
                                width,
                                height
                            },
                            croppedImage: {
                                mediaId: previewPhoto.media_id,
                                source: previewPhoto.url,
                                name: previewPhoto.media_id,
                                pixelCrop: {
                                    x: previewPhoto.crop_x,
                                    y: previewPhoto.crop_y,
                                    width: previewPhoto.crop_width ? previewPhoto.crop_width : width,
                                    height: previewPhoto.crop_height ? previewPhoto.crop_height : height
                                }
                            }
                        };
                    }

                    const rawGallery = photos.filter((photo) => {
                        return photo.is_gallery === true || (photo.is_primary === false && photo.is_preview === false)
                    });

                    const gallery = rawGallery.map((photo) => {
                        const width = parseInt(photo.width, 10);
                        const height = parseInt(photo.height, 10);

                        return {
                            id: photo.media_id,
                            altText: photo.title,
                            caption: photo.description,
                            credit: photo.credits,
                            position: photo.position,
                            created_date: photo.created_date,
                            updated_date: photo.updated_date,
                            is_gallery: photo.is_gallery,
                            is_preview: photo.is_preview,
                            is_primary: photo.is_primary,
                            originalImage: {
                                mediaId: photo.media_id,
                                source: photo.original_url,
                                name: photo.media_id,
                                width,
                                height
                            },
                            croppedImage: {
                                mediaId: photo.media_id,
                                source: photo.cropped_url,
                                name: photo.media_id,
                                pixelCrop: {
                                    x: photo.crop_x,
                                    y: photo.crop_y,
                                    width: photo.crop_width ? photo.crop_width : width,
                                    height: photo.crop_height ? photo.crop_height : height
                                }
                            }
                        };
                    });

                    // append hero photos to gallery
                    if (heroPhoto !== undefined) {
                        const existing = find(gallery, { id: hero.id });
                        if (!existing) {
                            gallery.push(hero)
                        }
                    }

                    // Sort the gallery photos
                    gallery.sort((a, b) => {
                        if (a.position < b.position) return -1;
                        if (b.position < a.position) return 1;
                        const keyA = Date.parse(a.created_date);
                        const keyB = Date.parse(b.created_date);
                        if (keyA < keyB) return -1;
                        if (keyA > keyB) return 1;
                        return 0;
                    });

                    this.setState({
                        hero,
                        preview,
                        gallery,
                        hasHeroImage: heroPhoto !== undefined
                    });
                }
                else {
                    // eslint-disable-next-line no-console
                    console.log('error', res.data.err);
                }
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.log('error', err);
            });
    };

    render() {
        const { hero, preview, hasHeroImage, gallery } = this.state;
        return (
            <div className="rec-photo-upload-page">
                <div className="rec-photo-upload-page-intro">
                    <h1>Photo Management</h1>
                    <p>
                        As part of the inventory management process through The
                        Hub, you’re able to add photos for your location. Please
                        use the photo tool to upload hero, preview, and gallery
                        images. The tool also includes a cropping feature that
                        lets you edit your photos and set the focus for how they
                        will appear on the site. A brief description and
                        additional instructions for each type of image are
                        provided under the informational button next to the
                        section titles below.
                    </p>
                    <p>
                        You must complete <strong>all</strong> fields before you can upload and
                        publish an image. The fields are:
                    </p>
                    <ul>
                        <li>
                            <strong>Alternative text:</strong> Alternative or
                            alt text is a key element of web
                            accessibility and is required for all images on
                            Recreation.gov. Alternative text isn&rsquo;t readily
                            visible on the site but is used by screen readers,
                            search engines, or when the image can&rsquo;t be loaded.
                        </li>
                        <li>
                            <strong>Caption text:</strong> The caption provides
                            additional details about the photo
                            that will serve as a banner below any images in the
                            &ldquo;Photo Gallery&rdquo; on your inventory page.
                        </li>
                        <li>
                            <strong>Photo credit:</strong> The photo credit
                            documents the individual or organization responsible
                            for taking the photo or who currently holds the rights
                            to the image.
                        </li>
                    </ul>
                    <p>
                        <span className="rec-photo-upload-required-mark">*</span> indicates a required field
                    </p>
                </div>

                <hr />

                {this.props.type === "asset" && this.props.heroEnabled &&
                    <HeroImages
                        primaryImage={hero}
                        gallery={gallery}
                        uploadManager={this.uploadManager}
                        onHeroPublish={this.onHeroPublish}
                        onHeroReload={this.loadMedia}
                        onHeroDelete={this.onHeroDelete}
                        onGalleryPublish={this.loadMedia}
                        onGalleryReload={this.loadMedia}
                        onGalleryDelete={this.loadMedia}
                        inventoryType={this.props.inventoryType}
                    />
                }

                <PreviewImage
                    originalImage={preview.originalImage}
                    croppedImage={preview.croppedImage}
                    altText={preview.altText}
                    caption={preview.caption}
                    credit={preview.credit}
                    uploadManager={this.uploadManager}
                    onPublish={this.loadMedia}
                    onReload={this.loadMedia}
                    onDelete={this.loadMedia}
                    hasHeroImage={hasHeroImage}
                    getHeroImage={this.getHeroImage} />

                {this.props.galleryEnabled &&
                <PhotoGalleryManager
                    gallery={gallery}
                    uploadManager={this.uploadManager}
                    saveToGallery={this.saveToGallery}
                    onDelete={this.loadMedia}
                    onReload={this.loadMedia}
                    onPublish={this.loadMedia}
                    onHeroPromotion={this.onHeroPromotion} />
                }
            </div>
        );
    }
}
